import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/public/SiteFooter";
import PageHero from "@/components/public/PageHero";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Package, MapPin, Clock, ArrowRight, RotateCcw, Star, Gift, Copy, FileText, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700", confirmed: "bg-blue-100 text-blue-700",
  collected: "bg-indigo-100 text-indigo-700", at_laundry: "bg-purple-100 text-purple-700",
  cleaning: "bg-purple-100 text-purple-700", ready: "bg-cyan-100 text-cyan-700",
  out_for_delivery: "bg-orange-100 text-orange-700", delivered: "bg-green-100 text-green-700",
  completed: "bg-green-100 text-green-700", cancelled: "bg-red-100 text-red-700",
};

function invoiceHtml(o) {
  const rows = o.items.map((li) => `<tr><td>${li.qty} × ${li.name}</td><td style="text-align:right">£${(li.qty * li.price).toFixed(2)}</td></tr>`).join("");
  return `<html><head><title>Invoice ${o.id.slice(0,8)}</title><style>body{font-family:Arial;padding:40px;color:#0f172a}h1{color:#2563EB}table{width:100%;border-collapse:collapse;margin-top:20px}td{padding:8px 0;border-bottom:1px solid #eee}.tot{font-weight:700;font-size:18px}</style></head>
  <body><h1>Just Laundry</h1><p>Invoice <b>#${o.id.slice(0,8).toUpperCase()}</b> · ${(o.created_at||'').slice(0,10)}</p>
  <p>${o.laundry_name}<br/>Customer: ${o.customer_name}<br/>${o.address}</p>
  <table>${rows}
  <tr><td>Delivery</td><td style="text-align:right">£${(o.delivery_fee||0).toFixed(2)}</td></tr>
  ${o.credit_applied?`<tr><td>Credit applied</td><td style="text-align:right">-£${o.credit_applied.toFixed(2)}</td></tr>`:''}
  <tr class="tot"><td>Total (${(o.currency||'gbp').toUpperCase()})</td><td style="text-align:right">£${o.total.toFixed(2)}</td></tr></table>
  <p style="margin-top:30px;color:#64748b">Payment: ${o.payment_status} · Status: ${o.status}</p>
  <script>window.print()</script></body></html>`;
}

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/orders").then((res) => setOrders(res.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const reorder = async (id) => {
    try {
      const { data: fresh } = await api.post(`/orders/${id}/reorder`);
      const { data } = await api.post("/payments/checkout", { order_id: fresh.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed"); }
  };

  const payNow = async (id) => {
    try {
      const { data } = await api.post("/payments/checkout", { order_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not start payment"); }
  };

  const isExpired = (o) => {
    const start = (o.pickup_slot || "").split("-")[0].trim();
    if (!o.pickup_date || start.length !== 5) return false;
    return new Date(`${o.pickup_date}T${start}:00`) < new Date();
  };

  const printInvoice = (o) => {
    const w = window.open("", "_blank");
    w.document.write(invoiceHtml(o));
    w.document.close();
  };

  const copyRef = () => { navigator.clipboard.writeText(user?.referral_code || ""); toast.success("Referral code copied!"); };
  const oc = useSite().content?.orders || {};

  return (
    <div className="public-site min-h-screen bg-slate-50">
      <Navbar dark />
      <PageHero overline={oc.overline} title={oc.title || "My orders"} subtitle={oc.subtitle}
        actions={<Link to="/search"><Button className="h-12 rounded-full bg-brand px-7 text-base font-bold hover:bg-brand-hover" data-testid="new-order-btn">New booking</Button></Link>} />
      <div className="mx-auto max-w-5xl px-5 py-10 lg:px-8">

        {/* Referral / credit */}
        <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-brand/20 bg-accent/40 p-6" data-testid="referral-banner">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand text-white"><Gift className="h-5 w-5" /></span>
            <div>
              <p className="font-heading text-lg font-medium text-slate-900">£{(user?.credit ?? 0).toFixed(2)} credit</p>
              <p className="text-sm text-slate-600">Share your code — you both get £5 when a friend signs up.</p>
            </div>
          </div>
          <button onClick={copyRef} className="flex items-center gap-2 rounded-lg border border-brand/30 bg-white px-4 py-2 font-mono text-sm font-semibold text-brand" data-testid="referral-code">
            {user?.referral_code || "—"} <Copy className="h-4 w-4" />
          </button>
        </div>

        {loading && <div className="mt-8 text-slate-400">Loading…</div>}
        {!loading && orders.length === 0 && (
          <div className="mt-10 rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="empty-orders">
            <Package className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-slate-500">No orders yet.</p>
            <Link to="/search"><Button className="mt-4 bg-brand hover:bg-brand-hover">Find a laundry <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-heading text-lg font-medium text-slate-900">{o.laundry_name}</p>
                  <p className="mt-1 font-mono text-xs text-slate-400">#{o.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-700"} border-0 capitalize`} data-testid={`order-status-${o.id}`}>{o.status.replace(/_/g, " ")}</Badge>
                  <Badge variant="outline" className={o.payment_status === "paid" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>{o.payment_status}</Badge>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Collect {o.pickup_date} · {o.pickup_slot}</span>
                {o.delivery_date && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> Deliver {o.delivery_date} · {o.delivery_slot}</span>}
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {o.address}</span>
              </div>

              <button onClick={() => setOpen(open === o.id ? null : o.id)} className="mt-3 flex items-center gap-1 text-sm font-medium text-brand" data-testid={`track-${o.id}`}>
                Track order <ChevronDown className={`h-4 w-4 transition-transform ${open === o.id ? "rotate-180" : ""}`} />
              </button>
              <ReportIssueDialog order={o} />
              {open === o.id && (
                <ol className="mt-3 space-y-3 border-l-2 border-slate-100 pl-4" data-testid={`timeline-${o.id}`}>
                  {(o.status_history || []).map((h, i) => (
                    <li key={i} className="relative">
                      <span className="absolute -left-[21px] top-1 h-3 w-3 rounded-full bg-brand ring-2 ring-white" />
                      <p className="text-sm font-medium capitalize text-slate-900">{h.status.replace(/_/g, " ")}</p>
                      <p className="text-xs text-slate-400">{h.note} · {(h.at || "").replace("T", " ").slice(0, 16)}</p>
                    </li>
                  ))}
                </ol>
              )}

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                <p className="text-sm text-slate-500">{o.items.reduce((a, b) => a + b.qty, 0)} items</p>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => printInvoice(o)} data-testid={`invoice-${o.id}`}><FileText className="mr-1.5 h-4 w-4" /> Invoice</Button>
                  <Button size="sm" variant="secondary" onClick={() => reorder(o.id)} data-testid={`reorder-${o.id}`}><RotateCcw className="mr-1.5 h-4 w-4" /> Reorder</Button>
                  {o.payment_status !== "paid" && o.status !== "cancelled" && !isExpired(o) && (
                    <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => payNow(o.id)} data-testid={`pay-now-${o.id}`}>Pay now</Button>
                  )}
                  {o.payment_status !== "paid" && o.status !== "cancelled" && isExpired(o) && (
                    <span className="text-xs font-medium text-slate-400" data-testid={`expired-${o.id}`}>Pickup time passed</span>
                  )}
                  {o.payment_status === "paid" && <ReviewDialog order={o} onDone={load} />}
                  <p className="font-heading text-xl font-semibold text-slate-900">£{o.total.toFixed(2)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function ReviewDialog({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(order.rating || 5);
  const [comment, setComment] = useState("");
  const submit = async () => {
    try { await api.post(`/orders/${order.id}/review`, { rating, comment }); toast.success("Thanks for your review!"); setOpen(false); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" data-testid={`review-${order.id}`}><Star className={`mr-1.5 h-4 w-4 ${order.reviewed ? "fill-amber-400 text-amber-400" : ""}`} /> {order.reviewed ? "Reviewed" : "Review"}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Rate {order.laundry_name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-1" data-testid="rating-stars">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`}>
                <Star className={`h-8 w-8 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the service?" data-testid="review-comment" />
        </div>
        <DialogFooter><Button onClick={submit} className="bg-brand hover:bg-brand-hover" data-testid="submit-review-btn">Submit review</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportIssueDialog({ order }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("laundry");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const submit = async () => {
    if (!subject || !message) return toast.error("Add a subject and a message");
    const against = target === "driver" && order.driver_id
      ? { against_type: "driver", against_id: order.driver_id, against_name: order.driver_name || "Driver" }
      : { against_type: "laundry", against_id: order.laundry_id, against_name: order.laundry_name };
    try {
      await api.post("/complaints", { ...against, order_id: order.id, subject, message });
      toast.success("Complaint submitted — our team will review it");
      setOpen(false); setSubject(""); setMessage("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="mt-3 ml-4 text-sm font-medium text-red-500 hover:underline" data-testid={`report-${order.id}`}>Report an issue</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Report an issue · order #{order.id.slice(0, 8).toUpperCase()}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-slate-700">Who is this about?</p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => setTarget("laundry")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${target === "laundry" ? "border-brand bg-accent text-brand" : "border-slate-200"}`} data-testid="report-target-laundry">The laundry</button>
              {order.driver_id && <button onClick={() => setTarget("driver")} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium ${target === "driver" ? "border-brand bg-accent text-brand" : "border-slate-200"}`} data-testid="report-target-driver">The driver</button>}
            </div>
          </div>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="report-subject" />
          <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe what went wrong…" data-testid="report-message" />
        </div>
        <DialogFooter><Button onClick={submit} className="bg-red-500 hover:bg-red-600" data-testid="report-submit">Submit complaint</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
